import { modelsService } from './models'
import { dataSourcesService } from './dataSources'
import { predictionsService } from './predictions'
import type { Model, DataSource, Prediction } from '@/types'

export interface SearchResult {
  id: string
  type: 'model' | 'data_source' | 'prediction'
  title: string
  description: string
  metadata?: Record<string, unknown>
  url: string
}

export interface GroupedSearchResults {
  models: SearchResult[]
  dataSources: SearchResult[]
  predictions: SearchResult[]
}

/**
 * Performs a global search across models, data sources, and predictions
 * @param query - The search query string
 * @returns Grouped search results
 */
export async function globalSearch(query: string): Promise<GroupedSearchResults> {
  if (!query || query.trim().length === 0) {
    return {
      models: [],
      dataSources: [],
      predictions: [],
    }
  }

  const normalizedQuery = query.toLowerCase().trim()

  try {
    // Fetch all data in parallel - all services return Result types
    const [modelsResult, dataSourcesResult, predictionsResult] = await Promise.all([
      modelsService.list(),
      dataSourcesService.list(),
      predictionsService.list({ page_size: 50 }),
    ])

    // Unwrap Result types with fallbacks for errors
    const modelsData = modelsResult.success ? modelsResult.data : { models: [], total: 0 }
    const dataSourcesData = dataSourcesResult.success ? dataSourcesResult.data : { data_sources: [], total: 0 }
    const predictionsData = predictionsResult.success ? predictionsResult.data : { items: [], total: 0 }

    // Search models
    const models: SearchResult[] = modelsData.models
      .filter((model: Model) => {
        const nameMatch = model.name.toLowerCase().includes(normalizedQuery)
        const descMatch = model.description?.toLowerCase().includes(normalizedQuery)
        const useCaseMatch = model.use_case_name.toLowerCase().includes(normalizedQuery)
        return nameMatch || descMatch || useCaseMatch
      })
      .map((model: Model) => ({
        id: model.id,
        type: 'model' as const,
        title: model.name,
        description: model.description || `${model.use_case_name} model`,
        metadata: {
          use_case: model.use_case_name,
          is_active: model.is_active,
          created_at: model.created_at,
        },
        url: `/app/models/${model.id}`,
      }))

    // Search data sources
    const dataSources: SearchResult[] = dataSourcesData.data_sources
      .filter((ds: DataSource) => {
        const name = ds.display_name || ''
        const connectorType = ds.connector_type || ds.connector_name || ''
        const nameMatch = name.toLowerCase().includes(normalizedQuery)
        const typeMatch = connectorType.toLowerCase().includes(normalizedQuery)
        return nameMatch || typeMatch
      })
      .map((ds: DataSource) => ({
        id: ds.id,
        type: 'data_source' as const,
        title: ds.display_name || 'Unknown',
        description: `${ds.connector_type || ds.connector_name || 'Unknown'} connector`,
        metadata: {
          connector_type: ds.connector_type || ds.connector_name,
          status: ds.status,
          last_sync_at: ds.last_sync_at,
        },
        url: `/app/data-sources/${ds.id}`,
      }))

    // Search predictions (API returns 'items' not 'predictions')
    const predictions: SearchResult[] = (predictionsData.items || [])
      .filter((pred: Prediction) => {
        const entityMatch = pred.entity_id.toLowerCase().includes(normalizedQuery)
        const labelMatch = pred.prediction_label?.toLowerCase().includes(normalizedQuery)
        return entityMatch || labelMatch
      })
      .map((pred: Prediction) => ({
        id: pred.id,
        type: 'prediction' as const,
        title: `Prediction for ${pred.entity_id}`,
        description: pred.prediction_label
          ? `${pred.prediction_label} (${(pred.prediction_value * 100).toFixed(1)}%)`
          : `Score: ${(pred.prediction_value * 100).toFixed(1)}%`,
        metadata: {
          entity_id: pred.entity_id,
          prediction_value: pred.prediction_value,
          confidence: pred.confidence,
          created_at: pred.created_at,
        },
        url: `/app/predictions/${pred.id}`,
      }))
      .slice(0, 10) // Limit predictions to top 10 most recent

    return {
      models,
      dataSources,
      predictions,
    }
  } catch (error) {
    console.error('Global search error:', error)
    return {
      models: [],
      dataSources: [],
      predictions: [],
    }
  }
}

/**
 * Gets the total count of search results
 */
export function getSearchResultCount(results: GroupedSearchResults): number {
  return results.models.length + results.dataSources.length + results.predictions.length
}

/**
 * Converts grouped results to a flat array for keyboard navigation
 */
export function flattenSearchResults(results: GroupedSearchResults): SearchResult[] {
  return [...results.models, ...results.dataSources, ...results.predictions]
}
